import { datadogRum } from '@datadog/browser-rum';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useHasAccess } from '../../hooks/useHasAccess';
import { useAssetManagement } from '../../hooks/useAssetManagement';
import { Event } from '../../interfaces';
import { eventHasVideos, getEventVideos, selectEventThumbnailUrl } from '../../utils/getEventVideos';
import { nothing } from '../../utils/nothing';
import { ZoomableImage } from '../atoms/imagesWithOverlays/zoomableImage';
import { InvisibleButton } from './components';

// TODO: Temporary SacRT-specific video styling.
// This applies object-fit: contain only for SacRT fleet
// Should be replaced with a more generic fleet-specific video configuration system.
const StyledVideo = styled.video<{ usesContain: boolean }>`
  object-fit: ${(props) => (props.usesContain ? 'contain' : 'cover')};
  background-color: ${(props) => (props.usesContain ? '#000' : 'initial')};
  border-radius: 2px;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1920 / 1080;
  max-height: 75vh;
  position: relative;

  &:focus {
    outline: none;
  }
`;

interface Props {
  event: Event;
  autoPlay?: boolean;
  className?: string;
  onClick?: (evt: any) => void;
  preloadVideo?: boolean;
}

export function EventVideo(props: Props) {
  const { event, preloadVideo } = props;
  const playerRef = React.useRef<HTMLVideoElement | null>(null);

  const videos = getEventVideos(event);
  const videoElement = playerRef.current?.video?.video;
  const hasAccess = useHasAccess();
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  const isAssetManagement = useAssetManagement();

  // given the presigned S3 URL, we memoize it to avoid unnecessary re-renders in case the event response
  // came from different API pod which would use a different presigner, effectively changing the URL

  const memoedVideoURL = useMemo(() => videos[0]?.presigned_s3_url, [event.id]);
  const memoedPoster = useMemo(() => videos[0]?.video_thumbnail_url, [event.id]);

  const startStopVideo = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const video = playerRef?.current;
    const isPlaying = video && video?.currentTime > 0 && !video.paused && !video.ended;

    if (!isPlaying) {
      video?.play();
    } else {
      video?.pause();
    }
  };

  useEffect(() => {
    setThumbnail(selectEventThumbnailUrl(props.event, hasAccess));
  }, [videoElement, event, hasAccess]);

  const reportLoadTime = (loadTime: number) => {
    datadogRum.addTiming('videoLoad', loadTime);
  };

  useEffect(() => {
    const video = playerRef.current;
    if (video) {
      const startTime = Date.now();
      const timeoutId = setTimeout(() => {
        reportLoadTime(15000); // Set load time to 15 seconds if timeout occurs
      }, 15000);

      const onLoadedData = () => {
        const endTime = Date.now();
        const loadTime = endTime - startTime;
        reportLoadTime(loadTime);
        clearTimeout(timeoutId);
      };

      video.addEventListener('loadeddata', onLoadedData);

      return () => {
        video.removeEventListener('loadeddata', onLoadedData);
        clearTimeout(timeoutId);
      };
    }

    return nothing;
  }, [memoedVideoURL]);

  // Short circuit if there are no videos or the first video is not FULLY published

  if (!eventHasVideos(event)) {
    return (
      <ZoomableImage
        disableZoom
        src={thumbnail ?? ''}
        event={event}
        drawLprPov
        ariaLabel={`Violation preview for ${event.license_plate}`}
      />
    );
  }

  return (
    <div className={`hdn-event-video__container s ${props.className}`}>
      <StyledVideo
        className="hdn-event-video__content"
        usesContain={isAssetManagement}
        autoPlay={props.autoPlay ?? true}
        muted
        loop
        poster={memoedPoster}
        src={memoedVideoURL}
        ref={playerRef}
        onClick={props.onClick ?? (() => false)}
        controls
        playsInline
        disablePictureInPicture
        controlsList="nofullscreen nodownload noremoteplayback"
        preload={preloadVideo ? 'auto' : 'metadata'}
      />
      <InvisibleButton onClick={startStopVideo} data-accesskey="Space" />
    </div>
  );
}
